import type { PromiseMethodDecorator } from '@egr/xbox/utils/Promise';

import { startNativeProgress, finishNativeProgress } from '@egr/xbox/app-api/AppAction';
import { withDebugTimer } from '@egr/xbox/utils/Debug';
import { getEnvBoolean } from '@egr/xbox/utils/ReactScriptHelper';
import { SyncExternalStore } from '@egr/xbox/utils/SyncExternalStore';

import { ListenableEvent } from '@easterngraphics/wcf/modules/utils';

const NATIVE_PROGRESS: boolean = getEnvBoolean('NATIVE_PROGRESS');

export class ProgressManagerClassBase<State extends { lockCount: number; } = { lockCount: number; }> extends SyncExternalStore<State> {
    private static globalLockCount = 0;
    private static readonly onLockCountChange = new ListenableEvent<-1 | 1, undefined>(undefined);
    static {
        ProgressManagerClassBase.onLockCountChange.addListener((value) => {
            ProgressManagerClass.globalLockCount += value;
        });

        (window as {_getGlobalLock?: () => Promise<void>})._getGlobalLock = async () => {
            if (ProgressManagerClass.globalLockCount === 0) {
                return Promise.resolve();
            }

            return new Promise((resolve: (data: void) => void) => {
                const eventHandler = () => {
                    if (ProgressManagerClass.globalLockCount === 0) {
                        ProgressManagerClass.onLockCountChange.removeListener(eventHandler);
                        resolve();
                    }
                };

                ProgressManagerClass.onLockCountChange.addListener(eventHandler);
            });
        };
    }

    constructor(
        protected name: string,
        initialState: State,
        protected progressDelay: number = 0,
    ) {
        super(initialState);
        this.startProgress = this.startProgress.bind(this);
        this.finishProgress = this.finishProgress.bind(this);
    }

    /** hook */
    public useProgress(): boolean {
        const { lockCount } = this.useState(['lockCount']);
        return lockCount !== 0;
    }

    /** no hook */
    public get inProgressState(): boolean {
        return this.snapshot.lockCount !== 0;
    }

    public startProgress(): void {
        this.updateState({ lockCount: ++this.snapshot.lockCount } as Partial<State>);
        ProgressManagerClass.onLockCountChange.trigger(1);

        if (NATIVE_PROGRESS) {
            startNativeProgress(this.name);
        }
    }

    public finishProgress(): void {
        if (this.snapshot.lockCount > 0) {
            this.updateState({ lockCount: --this.snapshot.lockCount } as Partial<State>);
            ProgressManagerClass.onLockCountChange.trigger(-1);
        }
        if (NATIVE_PROGRESS) {
            finishNativeProgress(this.name);
        }
    }

    public withProgress = async <T>(debugLabel: string, promise: Promise<T>): Promise<T> => {
        let delayedProgressTimeout: number | undefined;
        if (this.progressDelay > 0) {
            delayedProgressTimeout = window.setTimeout(
                () => {
                    delayedProgressTimeout = undefined;
                    this.startProgress();
                },
                this.progressDelay
            );
        } else {
            this.startProgress();
        }

        try {
            return await withDebugTimer(debugLabel, (): Promise<T> => {
                return promise;
            });
        } finally {
            if (delayedProgressTimeout != null) {
                window.clearTimeout(delayedProgressTimeout);
            } else {
                this.finishProgress();
            }
        }
    };

    public withInstantProgress = async <T>(debugLabel: string, promise: () => Promise<T>, ignoreProgress: boolean = false): Promise<T> => {
        let delayedProgressTimeout: number | undefined;
        if (ignoreProgress !== true) {
            if (this.progressDelay > 0) {
                delayedProgressTimeout = window.setTimeout(
                    () => {
                        delayedProgressTimeout = undefined;
                        this.startProgress();
                    },
                    this.progressDelay
                );
            } else {
                this.startProgress();
            }
        }

        try {
            return await withDebugTimer(debugLabel, (): Promise<T> => {
                return promise();
            });
        } finally {
            if (ignoreProgress !== true) {
                if (delayedProgressTimeout != null) {
                    window.clearTimeout(delayedProgressTimeout);
                } else {
                    this.finishProgress();
                }
            }
        }
    };
}

export class ProgressManagerClass extends ProgressManagerClassBase<{ lockCount: number }> {
    constructor(
        name: string,
        progressDelay: number = 0,
    ) {
        super(name, { lockCount: 0 }, progressDelay);
    }
}

export const ProgressManager: ProgressManagerClass = new ProgressManagerClass('progress');
export const HideProgressManager: ProgressManagerClass = new ProgressManagerClass('hideProgress');

export function wrapWithProgress<Args extends Array<unknown>, ReturnType>(
    label: string,
    callback: (...args: Args) => Promise<ReturnType>,
    customManager: ProgressManagerClass = ProgressManager
): (...args: Args) => Promise<ReturnType> {
    return (...args: Args): Promise<ReturnType> => {
        return customManager.withInstantProgress<ReturnType>(
            label,
            (): Promise<ReturnType> => {
                return callback(...args);
            }
        );
    };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function withProgress<T>(
    label: string,
    customManager: ProgressManagerClass = ProgressManager,
    ignoreProgress?: (self: unknown) => boolean
): PromiseMethodDecorator {
    return (
        target: object,
        propertyKey: string | symbol,
        descriptor: TypedPropertyDescriptor<(...args: Array<any>) => Promise<T>>
    ) => {
        const method: ((...args: Array<any>) => Promise<T>) | undefined = descriptor.value;
        if (method !== undefined) {
            descriptor.value = function(...args: Array<any>): Promise<T> {
                return customManager.withInstantProgress(
                    label,
                    (): Promise<T> => {
                        return method.apply(this, args);
                    },
                    ignoreProgress?.(this)
                );
            };
        }
        return descriptor;
    };
}
/* eslint-enable @typescript-eslint/no-explicit-any */
